import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, MapPin, Clock, AlertCircle, CheckCircle2, Ship, Truck, Plane, Info, Anchor, Box } from 'lucide-react';
import { motion } from 'framer-motion';
import { formatDisplayDate } from '../../utils/dateUtils';
import { baseURL } from '@/config/APIConfig';


const API = baseURL.dashboardApi;
// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// const API = `${BACKEND_URL}/api`;

const ContainerTracking = () => {
    const [containers, setContainers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedContainer, setSelectedContainer] = useState(null);

    useEffect(() => {
        fetchContainers();
    }, []);

    const fetchContainers = async () => {
        try {
            const response = await axios.get(`${API}/containers`);
            setContainers(response.data);
            if (response.data.length > 0) {
                setSelectedContainer(response.data[0]);
            }
        } catch (error) {
            console.error('Error fetching containers:', error);
        } finally {
            setLoading(false);
        }
    };

    // Enhanced timeline events based on the new design
    const getEnhancedTimeline = (container) => {
        const baseTimeline = container?.timeline || [];

        // Map the existing timeline to the new format
        return baseTimeline?.map((event, idx) => {
            let eventType = 'default';
            let icon = CheckCircle2;

            const eventLower = event.event.toLowerCase();
            if (eventLower.includes('departure') || eventLower.includes('departed')) {
                eventType = 'departure';
                icon = Ship;
            } else if (eventLower.includes('arrival') || eventLower.includes('arrived')) {
                eventType = 'arrival';
                icon = Anchor;
            } else if (eventLower.includes('load') || eventLower.includes('loaded')) {
                eventType = 'load';
                icon = Box;
            } else if (eventLower.includes('gate') || eventLower.includes('in gate')) {
                eventType = 'gate';
                icon = Package;
            } else if (eventLower.includes('pickup') || eventLower.includes('empty')) {
                eventType = 'pickup';
                icon = Truck;
            } else if (eventLower.includes('customs') || eventLower.includes('clearance')) {
                eventType = 'customs';
                icon = CheckCircle2;
            } else if (eventLower.includes('transit')) {
                eventType = 'transit';
                icon = Ship;
            } else if (eventLower.includes('deliver')) {
                eventType = 'delivery';
                icon = CheckCircle2;
            }

            return {
                ...event,
                eventType,
                icon,
                isLatest: idx === 0 && event.completed
            };
        });
    };

    // Get carrier logo based on vessel name
    const getCarrierInfo = (vesselName) => {
        const name = (vesselName || '').toUpperCase();
        if (name.includes('ONE') || name.includes('YM') || name.includes('YANG MING')) {
            return { name: 'ONE', color: 'from-pink-500 to-pink-600', textColor: 'text-pink-500' };
        }
        if (name.includes('MAERSK')) {
            return { name: 'MAERSK', color: 'from-sky-500 to-sky-600', textColor: 'text-sky-500' };
        }
        if (name.includes('MSC')) {
            return { name: 'MSC', color: 'from-blue-600 to-blue-700', textColor: 'text-blue-600' };
        }
        if (name.includes('CMA') || name.includes('CGM')) {
            return { name: 'CMA CGM', color: 'from-blue-700 to-blue-800', textColor: 'text-blue-700' };
        }
        if (name.includes('COSCO')) {
            return { name: 'COSCO', color: 'from-red-600 to-red-700', textColor: 'text-red-600' };
        }
        if (name.includes('EVERGREEN')) {
            return { name: 'EVERGREEN', color: 'from-green-600 to-green-700', textColor: 'text-green-600' };
        }
        if (name.includes('HAPAG')) {
            return { name: 'HAPAG-LLOYD', color: 'from-orange-500 to-orange-600', textColor: 'text-orange-500' };
        }
        return { name: 'CARRIER', color: 'from-slate-500 to-slate-600', textColor: 'text-slate-500' };
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 animate-pulse">
                    <div className="h-48 bg-slate-200 dark:bg-slate-800 rounded"></div>
                </div>
            </div>
        );
    }

    const carrierInfo = selectedContainer ? getCarrierInfo(selectedContainer?.vessel_name) : null;
    const enhancedTimeline = selectedContainer ? getEnhancedTimeline(selectedContainer) : [];

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
            data-testid="container-tracking"
        >
            {/* AI Insight Banner */}
            <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl p-6 text-white shadow-xl">
                <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                        <Package className="w-6 h-6" />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold mb-2">Container Intelligence</h3>
                        <p className="text-white/90">
                            Container {selectedContainer?.container_id || 'XYZ123'} delayed by 1.5 days due to port congestion.
                            <span className="font-bold">Suggest switching carrier MSC → Hapag-Lloyd for better on-time reliability (92%).</span>
                        </p>
                    </div>
                </div>
            </div>

            {/* Selected Container Detail - New Design */}
            {selectedContainer && (
                <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
                    {/* Header with Carrier Logo */}
                    <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                {/* Carrier Logo */}
                                <div className={`px-4 py-2 bg-gradient-to-r ${carrierInfo.color} rounded-lg`}>
                                    <span className="text-white font-bold text-lg">{carrierInfo.name}</span>
                                </div>
                                <div>
                                    <div className="flex items-center space-x-2">
                                        <span className="text-xl font-bold text-slate-900 dark:text-white">
                                            {selectedContainer?.container_id}
                                        </span>
                                        <span className="text-slate-400">—</span>
                                        <span className="text-slate-600 dark:text-slate-400">{selectedContainer?.vessel_name}</span>
                                    </div>
                                </div>
                            </div>
                            {/* Status Badge */}
                            <div className={`px-4 py-2 rounded-lg font-bold text-sm ${selectedContainer?.status === 'Delivered'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                : selectedContainer?.status === 'In Transit'
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                    : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                                }`}>
                                {selectedContainer?.status === 'In Transit' ? 'ON TIME' : selectedContainer?.status?.toUpperCase()}
                            </div>
                        </div>
                    </div>

                    {/* Route Visualization */}
                    <div className="p-6 bg-slate-50 dark:bg-slate-800/50">
                        <div className="relative">
                            {/* Route Line */}
                            <div className="flex items-center justify-between">
                                {/* Origin */}
                                <div className="flex flex-col items-center z-10">
                                    <div className="w-12 h-12 bg-white dark:bg-slate-700 rounded-full border-4 border-blue-500 flex items-center justify-center shadow-md">
                                        <Truck className="w-5 h-5 text-blue-500" />
                                    </div>
                                    <div className="mt-3 text-center">
                                        <div className="text-sm font-bold text-slate-900 dark:text-white">{selectedContainer?.origin}</div>
                                        <div className="text-xs text-slate-500">Origin</div>
                                    </div>
                                </div>

                                {/* Route Line with Ship */}
                                <div className="flex-1 mx-4 relative">
                                    <div className="absolute top-6 left-0 right-0 h-1 bg-blue-200 dark:bg-blue-900"></div>
                                    <div className="absolute top-6 left-0 h-1 bg-blue-500" style={{ width: `${Math.min(100, (selectedContainer?.delay_probability || 0.5) * 100 + 50)}%` }}></div>

                                    {/* Ship Icon on Route */}
                                    <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10">
                                        <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center shadow-lg border-4 border-white dark:border-slate-800">
                                            <Ship className="w-7 h-7 text-white" />
                                        </div>
                                        <div className="mt-2 text-center">
                                            <div className="text-xs font-medium text-slate-700 dark:text-slate-300">{selectedContainer?.vessel_name}</div>
                                        </div>
                                    </div>

                                    {/* Dates */}
                                    <div className="flex justify-between pt-20">
                                        <div className="text-center">
                                            <div className="text-xs text-slate-400 uppercase">Estimate</div>
                                            <div className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                                {formatDisplayDate(selectedContainer?.etd || new Date().toISOString())}
                                            </div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-xs text-slate-400 uppercase">ETA</div>
                                            <div className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                                {formatDisplayDate(selectedContainer?.eta)}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Destination */}
                                <div className="flex flex-col items-center z-10">
                                    <div className="w-12 h-12 bg-white dark:bg-slate-700 rounded-full border-4 border-green-500 flex items-center justify-center shadow-md">
                                        <MapPin className="w-5 h-5 text-green-500" />
                                    </div>
                                    <div className="mt-3 text-center">
                                        <div className="text-sm font-bold text-slate-900 dark:text-white">{selectedContainer?.destination}</div>
                                        <div className="text-xs text-slate-500">Destination</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Detailed Timeline - New Design */}
                    <div className="p-6">
                        <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-6 flex items-center">
                            <Clock className="w-5 h-5 mr-2 text-blue-500" />
                            Shipment Events
                        </h4>

                        {/* Timeline grouped by location */}
                        <div className="relative">
                            {/* Main vertical line */}
                            <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-blue-200 dark:bg-blue-900"></div>

                            {/* Origin Port Events */}
                            <div className="mb-8">
                                <div className="flex items-center mb-4">
                                    <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center z-10">
                                        <MapPin className="w-5 h-5 text-white" />
                                    </div>
                                    <h5 className="ml-4 text-base font-bold text-slate-900 dark:text-white uppercase tracking-wide">
                                        {selectedContainer?.origin}
                                    </h5>
                                </div>

                                <div className="ml-14 space-y-4">
                                    {/* Vessel/Flight Departure */}
                                    <div className="flex items-start justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                                        <div className="flex items-start space-x-3">
                                            <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                                                <Ship className="w-4 h-4 text-blue-600" />
                                            </div>
                                            <div>
                                                <div className="flex items-center space-x-2">
                                                    <span className="font-semibold text-slate-900 dark:text-white">VESSEL/FLIGHT DEPARTURE (ATD)</span>
                                                    <span className="px-2 py-0.5 bg-yellow-400 text-yellow-900 text-xs font-bold rounded">LATEST</span>
                                                </div>
                                                <div className="flex items-center mt-1 text-sm text-slate-600 dark:text-slate-400">
                                                    <Ship className="w-4 h-4 mr-1" />
                                                    {selectedContainer?.vessel_name}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <button className="w-6 h-6 bg-slate-200 dark:bg-slate-700 rounded-full flex items-center justify-center hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors">
                                                <Info className="w-3 h-3 text-slate-600 dark:text-slate-400" />
                                            </button>
                                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                                {enhancedTimeline[0]?.date ? new Date(enhancedTimeline[0].date).toLocaleString() : '10-26-2025 16:45'}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Container Load on Vessel */}
                                    <div className="flex items-start justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                                        <div className="flex items-start space-x-3">
                                            <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                                                <Box className="w-4 h-4 text-green-600" />
                                            </div>
                                            <div>
                                                <span className="font-semibold text-slate-900 dark:text-white">CONTAINER LOAD ON VESSEL</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <button className="w-6 h-6 bg-slate-200 dark:bg-slate-700 rounded-full flex items-center justify-center hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors">
                                                <Info className="w-3 h-3 text-slate-600 dark:text-slate-400" />
                                            </button>
                                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                                {enhancedTimeline[1]?.date ? new Date(enhancedTimeline[1].date).toLocaleString() : '10-26-2025 08:37'}
                                            </span>
                                        </div>
                                    </div>

                                    {/* In Gate */}
                                    <div className="flex items-start justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                                        <div className="flex items-start space-x-3">
                                            <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                                                <Package className="w-4 h-4 text-purple-600" />
                                            </div>
                                            <div>
                                                <span className="font-semibold text-slate-900 dark:text-white">IN GATE</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <button className="w-6 h-6 bg-slate-200 dark:bg-slate-700 rounded-full flex items-center justify-center hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors">
                                                <Info className="w-3 h-3 text-slate-600 dark:text-slate-400" />
                                            </button>
                                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                                {enhancedTimeline[2]?.date ? new Date(enhancedTimeline[2].date).toLocaleString() : '10-15-2025 17:28'}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Empty Container Pickup */}
                                    <div className="flex items-start justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                                        <div className="flex items-start space-x-3">
                                            <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                                                <Truck className="w-4 h-4 text-orange-600" />
                                            </div>
                                            <div>
                                                <span className="font-semibold text-slate-900 dark:text-white">EMPTY CONTAINER PICKUP</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <button className="w-6 h-6 bg-slate-200 dark:bg-slate-700 rounded-full flex items-center justify-center hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors">
                                                <Info className="w-3 h-3 text-slate-600 dark:text-slate-400" />
                                            </button>
                                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                                {enhancedTimeline[3]?.date ? new Date(enhancedTimeline[3].date).toLocaleString() : '10-14-2025 22:51'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Destination Port Events */}
                            <div className="mb-8">
                                <div className="flex items-center mb-4">
                                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center z-10">
                                        <MapPin className="w-5 h-5 text-white" />
                                    </div>
                                    <h5 className="ml-4 text-base font-bold text-slate-900 dark:text-white uppercase tracking-wide">
                                        {selectedContainer?.destination}
                                    </h5>
                                </div>

                                <div className="ml-14 space-y-4">
                                    {/* Vessel/Flight Arrival */}
                                    <div className="flex items-start justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                                        <div className="flex items-start space-x-3">
                                            <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                                                <Anchor className="w-4 h-4 text-blue-600" />
                                            </div>
                                            <div>
                                                <span className="font-semibold text-slate-900 dark:text-white">VESSEL/FLIGHT ARRIVAL (ATA)</span>
                                                <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">DELIVER TO CONSIGNEE OR AGENT</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <button className="w-6 h-6 bg-slate-200 dark:bg-slate-700 rounded-full flex items-center justify-center hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors">
                                                <Info className="w-3 h-3 text-slate-600 dark:text-slate-400" />
                                            </button>
                                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                                {formatDisplayDate(selectedContainer?.eta)} (EST)
                                            </span>
                                        </div>
                                    </div>

                                    {/* Out Gate */}
                                    <div className="flex items-start justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                                        <div className="flex items-start space-x-3">
                                            <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                                            </div>
                                            <div>
                                                <span className="font-semibold text-slate-900 dark:text-white">OUT GATE / DELIVERY</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <button className="w-6 h-6 bg-slate-200 dark:bg-slate-700 rounded-full flex items-center justify-center hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors">
                                                <Info className="w-3 h-3 text-slate-600 dark:text-slate-400" />
                                            </button>
                                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Pending</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Delay Probability - Updated Design */}
                    <div className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border-t border-orange-200 dark:border-orange-800">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                                    <AlertCircle className="w-5 h-5 text-white" />
                                </div>
                                <span className="font-bold text-slate-900 dark:text-white">Delay Probability</span>
                            </div>
                            <span className="text-3xl font-bold text-orange-500">
                                {(selectedContainer?.delay_probability * 100).toFixed(0)}%
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* All Containers List */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-slate-200 dark:border-slate-800">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6">All Containers</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Array.isArray(containers) && containers?.map((container) => {
                        const containerCarrier = getCarrierInfo(container.vessel_name);
                        return (
                            <div
                                key={container.id}
                                onClick={() => setSelectedContainer(container)}
                                data-testid={`container-card-${container.id}`}
                                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${selectedContainer?.id === container.id
                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                    : 'border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-700'
                                    }`}
                            >
                                <div className="flex items-start justify-between mb-3">
                                    <div>
                                        <div className="font-bold text-slate-900 dark:text-white">{container.container_id}</div>
                                        <div className="text-xs text-slate-500 dark:text-slate-400">{container.vessel_name}</div>
                                    </div>
                                    <div className={`px-2 py-1 rounded text-xs font-bold bg-gradient-to-r ${containerCarrier.color} text-white`}>
                                        {containerCarrier.name}
                                    </div>
                                </div>
                                <div className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                                    {container.origin} → {container.destination}
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${container.status === 'Delivered' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                                        container.status === 'In Transit' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' :
                                            'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400'
                                        }`}>
                                        {container.status}
                                    </span>
                                    <span className="text-xs text-slate-500 dark:text-slate-400">
                                        {formatDisplayDate(container.eta)}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </motion.div>
    );
};

export default ContainerTracking;
